import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import FileText from "@insite/mobius/Icons/FileText";
import Image from "@insite/mobius/Icons/Image";
import XCircle from "@insite/mobius/Icons/XCircle";
import { ListItem, ListItemProps, UnorderedListProps } from "@insite/mobius/Lists";
import UnorderedList from "@insite/mobius/Lists/UnorderedList";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getRmaAttachedFiles, getRmaTotalFilesSize } from "@store/Selectors/Rma/RmaSelectors";
import addFileToRma from "@store/Store/Data/RmaRequest/Handlers/AddFileToRma";
import removeFileFromRma from "@store/Store/Data/RmaRequest/Handlers/RemoveFileFromRma";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { RmaAttachedFileModel } from "@store/Types/ApiModels";
import arrayBufferToBase64 from "@store/utilities/arrayBufferToBase64";
import join from "lodash/join";
import union from "lodash/union";
import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line
// @ts-ignore
import bytes from "bytes";

interface OwnProps {
    extendedStyles?: RmaUploadFileStyles;
    supportedFileExtensions?: string[];
}

const mapStateToProps = (state: StoreApplicationState) => ({
    cdnPrefix: getSettingsCollection(state)?.websiteSettings.cdnPrefix,
    rmaAttachedFiles: getRmaAttachedFiles(state),
    rmaTotalFilesSize: getRmaTotalFilesSize(state),
});

const mapDispatchToProps = {
    addFileToRma,
    removeFileFromRma,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface RmaUploadFileStyles {
    wrapper?: InjectableCss;
    headerLabel?: TypographyPresentationProps;
    actionsContainer?: InjectableCss;
    downloadPdfButton?: ButtonPresentationProps;
    fileUpload?: FileUploadPresentationProps;
    fileList?: UnorderedListProps;
    fileListItem?: ListItemProps;
    fileName?: TypographyPresentationProps;
    removeFileClickable?: ClickableProps;
    removeFileIcon?: IconPresentationProps;
}

export const rmaUploadFileStyles: RmaUploadFileStyles = {
    headerLabel: {
        weight: 700,
    },
    actionsContainer: {
        css: css`
            display: inline-flex;
            justify-content: start;
            gap: 8px;
            align-items: start;
            width: 100%;
            margin-top: 16px;
            margin-bottom: 8px;
        `,
    },
    downloadPdfButton: {
        variant: "tertiary",
        css: css`
            width: fit-content;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    fileUpload: {
        sizeVariant: "small",
        buttonProps: {
            css: css`
                width: fit-content;
                margin-left: 0;
            `,
        },
        cssOverrides: {
            formField: css`
                display: none;
                width: 0;
                height: 0;
                margin: 0;
            `,
        },
    },
    fileList: {
        css: css`
            list-style-type: none;
            padding: 0;
            margin: 0;
        `,
    },
    fileListItem: {
        css: css`
            display: flex;
            align-content: center;
            text-align: center;
            gap: 4px;
            margin-bottom: 8px;
        `,
    },
    removeFileIcon: {
        src: XCircle,
    },
};

const RmaUploadFile = ({
    extendedStyles,
    cdnPrefix,
    rmaAttachedFiles,
    addFileToRma,
    removeFileFromRma,
    supportedFileExtensions,
    rmaTotalFilesSize,
}: Props) => {
    const [styles] = useState(() => mergeToNew(rmaUploadFileStyles, extendedStyles));
    const toasterCtx = useToaster();

    const accept = useMemo(
        () => join(union([".jpg", ".jpeg", ".png", ".bmp", ".pdf"], supportedFileExtensions)),
        [supportedFileExtensions],
    );

    const getFileUrl = useCallback((fileName: string) => `${cdnPrefix}/userfiles/pdf/rma/${fileName}`, [cdnPrefix]);

    const handleUploadFiles = (evt: ChangeEvent<any>) => {
        const files = evt.target.files as File[];

        if (!files || files.length === 0) {
            return;
        }

        const errors: string[] = [];
        let cumulatedFileSize = rmaTotalFilesSize;
        for (const file of files) {
            if (accept.indexOf(`.${getFileExtension(file.name)}`) === -1) {
                errors.push(translate("rmaFileExtensionNotAllowed", file.name));
                continue;
            } else if (file.size > 4e6) {
                errors.push(translate("rmaFileTooLarge", file.name, bytes(file.size)!));
                continue;
            } else if (cumulatedFileSize + file.size > 20e6) {
                errors.push(
                    translate(
                        "rmaTotalFilesTooLarge",
                        file.name,
                        bytes(file.size)!,
                        bytes(cumulatedFileSize + file.size)!,
                    ),
                );
                continue;
            }

            cumulatedFileSize += file.size;

            const reader = new FileReader();
            reader.onload = onFileReaderLoad(file.name);
            reader.readAsArrayBuffer(file);
        }

        if (errors.length > 0) {
            toasterCtx.addToast({ messageType: "danger", body: errors.join("<br/>") });
        }
    };

    const onFileReaderLoad = (fileName: string) => (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result as ArrayBuffer | null;
        if (!data) {
            return;
        }

        const base64Data = arrayBufferToBase64(data);
        const newFile: RmaAttachedFileModel = {
            Name: fileName,
            Content: base64Data,
            Size: data.byteLength,
        };

        addFileToRma({ newFile });
    };

    const handleDownloadPdfFile = () => {
        window.open(getFileUrl(translate("defectPartFormFileName")));
    };

    const handleRemoveFile = (fileName: string) => {
        removeFileFromRma({ fileName });
    };

    const fileIcon = (fileExtension: string) => (fileExtension === "pdf" ? <FileText /> : <Image />);

    const fileList = useMemo(
        () =>
            rmaAttachedFiles.map(file => {
                const extension = getFileExtension(file.Name);
                return (
                    <ListItem {...styles.fileListItem} key={file.Name}>
                        <Clickable {...styles.removeFileClickable} onClick={() => handleRemoveFile(file.Name)}>
                            <IconMemo {...styles.removeFileIcon} />
                        </Clickable>

                        {fileIcon(extension)}
                        <Typography as="span">{file.Name}</Typography>
                        <Typography as="span">{bytes(file.Size)}</Typography>
                    </ListItem>
                );
            }),
        [rmaAttachedFiles],
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.headerLabel} as="h3">
                {translate("Pictures and Defect Part form")}
            </Typography>
            <StyledWrapper {...styles.actionsContainer}>
                <FileUpload
                    {...styles.fileUpload}
                    multiple
                    accept={accept}
                    onFileChange={handleUploadFiles}
                    buttonText={translate("Upload File(s)")}
                />
                <Button {...styles.downloadPdfButton} onClick={handleDownloadPdfFile}>
                    {translate("Download Parts Defect Form")}
                </Button>
            </StyledWrapper>
            <UnorderedList {...styles.fileList}>{fileList}</UnorderedList>
            {bytes(rmaTotalFilesSize)}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RmaUploadFile);
